import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Layers } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { useProjectStore } from '../../stores/projectStore'

interface CreateProjectDialogProps {
  open: boolean
  onClose: () => void
}

const CreateProjectDialog: React.FC<CreateProjectDialogProps> = ({ open, onClose }) => {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [aspectRatio, setAspectRatio] = useState('16:9')

  const { user } = useAuthStore()
  const { createProject, loading, error, clearError } = useProjectStore()

  const aspectRatios = [
    { value: '16:9', label: 'Widescreen (16:9)' },
    { value: '4:3', label: 'Standard (4:3)' },
    { value: '4:5', label: 'Portrait (4:5)' },
    { value: '1:1', label: 'Square (1:1)' },
    { value: '9:16', label: 'Vertical (9:16)' },
    { value: 'custom', label: 'Custom' },
  ]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!user) return

    try {
      await createProject({
        title,
        description,
        aspectRatio,
        ownerId: user.uid,
        sharedWith: [],
        totalScenes: 0,
        totalImages: 0,
        isPublic: false,
        allowComments: true,
      })
      
      // Reset form and close dialog
      setTitle('')
      setDescription('')
      setAspectRatio('16:9')
      onClose()
    } catch (error) {
      // Error is handled by the store
    }
  }

  const handleClose = () => {
    clearError()
    setTitle('')
    setDescription('')
    setAspectRatio('16:9')
    onClose()
  }

  if (!open) return null

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        />

        {/* Dialog */}
        <motion.div
          className="relative glass-card w-full max-w-lg"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: 'spring', duration: 0.5 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 glass-card flex items-center justify-center">
                <Layers className="w-5 h-5 text-primary-300" />
              </div>
              <h2 className="text-2xl font-bold text-white">Create New Project</h2>
            </div>
            <button
              onClick={handleClose}
              className="glass-button p-2 hover:bg-white/20"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <motion.div
                className="bg-red-500/20 border border-red-500/30 rounded-lg p-3"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <p className="text-red-200 text-sm">{error}</p>
              </motion.div>
            )}

            <div>
              <label className="block text-white/90 text-sm font-medium mb-2">
                Project Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="glass-input w-full"
                placeholder="Enter project title"
                required
              />
            </div>

            <div>
              <label className="block text-white/90 text-sm font-medium mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="glass-input w-full h-24 resize-none"
                placeholder="Describe your project..."
              />
            </div>

            <div>
              <label className="block text-white/90 text-sm font-medium mb-2">
                Aspect Ratio
              </label>
              <select
                value={aspectRatio}
                onChange={(e) => setAspectRatio(e.target.value)}
                className="glass-input w-full"
              >
                {aspectRatios.map((ratio) => (
                  <option key={ratio.value} value={ratio.value} className="bg-gray-800">
                    {ratio.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 glass-button text-white/90 hover:bg-white/10 py-3"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !title.trim()}
                className="flex-1 glass-button bg-primary-500/30 hover:bg-primary-500/40 text-white font-semibold py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default CreateProjectDialog 